import {
    type ReactNode,
    useState,
    useMemo,
    useRef
} from "react";
import {
    type PanResponderGestureState,
    PanResponder,
    StyleSheet,
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import {
    MOTION_ICON_APPEAR_SCALE,
    MOTION_ICON_APPEAR_BLUR,
    type MotionIntentType,
    type MotionEasingType,
    MOTION_SURFACE_SCALE,
    useInteractionState,
    type NCoreUIKitIcon,
    MOTION_PRESS_SCALE,
    MOTION_CSS_EASINGS,
    getMotionDuration,
    getFocusRingStyle,
    type MotionSpring,
    useReducedMotion,
    useAnimatedState,
    NCoreUIKitTheme,
    getAppearStyle,
    MOTION_EASINGS,
    MOTION_INTENTS,
    MOTION_SPRINGS,
    getCornerStyle,
    getSlideStyle,
    usePressScale,
    AnimatedIcon,
    DropdownMenu,
    ToggleGroup,
    RadioButton,
    usePresence,
    CheckBox,
    webStyle,
    Popover,
    Switch,
    Button,
    Badge,
    Chip,
    Card,
    Text
} from "ncore-ui-kit";
import {
    getKitLocale,
    useDocsI18n
} from "../i18n";
import type {
    DocsTranslate
} from "../anatomy/types";
import type {
    DocsTocItem
} from "../components/toc";
import {
    BookmarkCheck as BookmarkCheckIcon,
    ChevronRight as ChevronRightIcon,
    ChevronDown as ChevronDownIcon,
    ArrowUpDown as ArrowUpDownIcon,
    CheckCheck as CheckCheckIcon,
    ChevronUp as ChevronUpIcon,
    ArrowDown as ArrowDownIcon,
    EyeClosed as EyeClosedIcon,
    Settings as SettingsIcon,
    Bookmark as BookmarkIcon,
    LockOpen as LockOpenIcon,
    VolumeX as VolumeXIcon,
    Volume2 as Volume2Icon,
    WifiOff as WifiOffIcon,
    ArrowUp as ArrowUpIcon,
    BellOff as BellOffIcon,
    Trash2 as Trash2Icon,
    Search as SearchIcon,
    MicOff as MicOffIcon,
    EyeOff as EyeOffIcon,
    Minus as MinusIcon,
    Check as CheckIcon,
    Wifi as WifiIcon,
    Copy as CopyIcon,
    Menu as MenuIcon,
    Plus as PlusIcon,
    Moon as MoonIcon,
    Bell as BellIcon,
    Lock as LockIcon,
    type LucideIcon,
    Mic as MicIcon,
    Sun as SunIcon,
    Eye as EyeIcon,
    X as XIcon
} from "lucide-react-native";
import {
    PageHeader,
    BulletList,
    InlineCode,
    Paragraph,
    DocsTable,
    CodeBlock,
    SubTitle,
    Article,
    Section,
    Preview,
    Cards,
    Stats
} from "../components/content";
import {
    createTransitionIcon
} from "../components/kitIcons";

type BezierType = [number, number, number, number];

type EasingCurveType = {
    descriptionKey: string;
    name: MotionEasingType;
    bezier: BezierType;
};

type ExtraIntentType = "iconSignature" | "dismissSwipe" | "attention" | "loading" | "focus";

type DurationNameType = "stagger" | "breathe" | "medium" | "short" | "blink" | "long" | "spin" | "loop";

type IntentRowType = {
    intents: Array<MotionIntentType | ExtraIntentType>;
    moveDurations?: Array<DurationNameType>;
    moveArgs?: Array<number>;
    whereKey?: string;
    reduceKey: string;
    timeKey?: string;
    moveKey: string;
    where?: string;
    time?: string;
};

type IntentPurposeType = {
    intent: MotionIntentType;
    key: string;
};

type DurationTokenType = {
    name: DurationNameType;
    key: string;
};

type HelperRowType = {
    names: Array<string>;
    kindKey: string;
    textKey: string;
};

type MorphPairType = ReturnType<typeof createTransitionIcon> & {
    fromName: string;
    toName: string;
    label: string;
};

type StickerItemType = {
    statusIndex: number;
    isVisible: boolean;
    isAdded: boolean;
    id: number;
};

const SPRING_CODE = `const isReduceMotionEnabled = useReducedMotion();

const handleRelease = (gesture: PanResponderGestureState) => {
    if(isReduceMotionEnabled) {
        translateX.setValue(0);

        return;
    }

    Animated.spring(translateX, {
        ...MOTION_SPRINGS.standard,
        useNativeDriver: Platform.OS !== "web",
        velocity: gesture.vx,
        toValue: 0
    }).start();
};`;

const PRESS_CODE = `const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const {
    interactionHandlers,
    isPressed
} = useInteractionState();

const pressScaleStyle = usePressScale({
    isPressed
});

<AnimatedPressable
    {...interactionHandlers}
    style={[styles.control, pressScaleStyle]}
/>`;

const TOGGLE_CODE = `const dotScale = useAnimatedState({
    value: isSelected ? 1 : 0,
    intent: "toggle"
});

<Animated.View
    style={{
        transform: [
            {
                scale: dotScale
            }
        ]
    }}
/>`;

const APPEAR_CODE = `const {
    isReduceMotionEnabled,
    isRendered,
    progress
} = usePresence({
    enterIntent: "iconAppear",
    exitIntent: "iconAppear",
    isVisible: hasSelection
});

{
    isRendered ?
        <Animated.View
            style={getAppearStyle({
                isReduceMotionEnabled,
                progress
            })}
        >
            <ClearButton/>
        </Animated.View>
    :
        null
}`;

const LIST_CODE = `const BadgeItem = ({
    onExited,
    item
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        isInitialAnimated: item.isAdded,
        isVisible: item.isVisible,
        enterIntent: "listInsert",
        exitIntent: "listRemove",
        onExited
    });

    if(!isRendered) {
        return null;
    }

    return <Animated.View
        style={getSlideStyle({
            offset: spaces.spacingSm,
            isReduceMotionEnabled,
            progress
        })}
    >
        <Badge title={item.title}/>
    </Animated.View>;
};`;

const SWAP_CODE = `const {
    spaces
} = NCoreUIKitTheme.useContext();

const first = usePresence({
    isVisible: panel === "first",
    enterIntent: "contentSwap",
    exitIntent: "contentSwap"
});

const second = usePresence({
    isVisible: panel === "second",
    enterIntent: "contentSwap",
    exitIntent: "contentSwap"
});

{
    first.isRendered ?
        <Animated.View
            style={[
                styles.panel,
                getSlideStyle({
                    isReduceMotionEnabled: first.isReduceMotionEnabled,
                    offset: -spaces.spacingSm,
                    progress: first.progress
                })
            ]}
        >
            <FirstPanel/>
        </Animated.View>
    :
        null
}`;

const MORPH_CODE = `<AnimatedIcon
    isTransitioned={isHidden}
    iconTransition="morph"
    fromIcon={EyeIcon}
    toIcon={EyeClosedIcon}
/>`;

const SIGNATURE_CODE = `const CloseIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <AnimatedIcon
    animationTrigger="hover"
    customColor={customColor}
    animation="signature"
    icon={XIcon}
    size={size}
/>;

<Button
    accessibilityLabel="Close"
    onPress={handleClose}
    icon={CloseIcon}
    variant="ghost"
/>`;

const THEME_CODE = `suppressWebTransitions();

document.documentElement.classList.toggle("high-contrast");`;

const TIMING_CODE = `const {
    duration,
    easing
} = useMotionTiming("contentSwap", undefined, "fade");

Animated.timing(opacity, {
    useNativeDriver: Platform.OS !== "web",
    toValue: 1,
    duration,
    easing
}).start();

const {
    durations
} = NCoreUIKitTheme.useContext();

const isReduceMotionEnabled = useReducedMotion();

const timing = getMotionTiming({
    isReduceMotionEnabled,
    intent: "listInsert",
    durations
});`;

const ANIMATED_STATE_CODE = `const opacity = useAnimatedState({
    onFinish: () => setIsSettled(true),
    value: isExpanded ? 1 : 0,
    intent: "contentSwap",
    motion: "fade"
});

<Animated.View
    style={{
        opacity
    }}
>
    <Details/>
</Animated.View>`;

const PRESENCE_CODE = `const {
    spaces
} = NCoreUIKitTheme.useContext();

const {
    isReduceMotionEnabled,
    isRendered,
    progress
} = usePresence({
    onExited: () => setIsDismissed(true),
    isVisible: hasUnsavedChanges
});

if(!isRendered) {
    return null;
}

return <Animated.View
    style={getSlideStyle({
        offset: spaces.spacingSm,
        isReduceMotionEnabled,
        progress
    })}
>
    <UnsavedChangesNotice/>
</Animated.View>;`;

const CROSSFADE_CODE = `const isReduceMotionEnabled = useReducedMotion();

const progress = useAnimatedState({
    value: isCopied ? 1 : 0,
    intent: "iconSwap"
});

const {
    outgoing,
    incoming
} = getCrossfadeStyle({
    isReduceMotionEnabled,
    progress
});

<View>
    <Animated.View style={outgoing}>
        <CopyIcon/>
    </Animated.View>
    <Animated.View style={[styles.layer, incoming]}>
        <CheckIcon/>
    </Animated.View>
</View>`;

const TRANSITION_CODE = `const {
    durations
} = NCoreUIKitTheme.useContext();

const isReduceMotionEnabled = useReducedMotion();

const transition = getMotionTransition({
    properties: [
        "background-color",
        "border-color"
    ],
    isReduceMotionEnabled,
    intent: "hover",
    durations
});

const style = [
    styles.container,
    webStyle(transition)
];`;

const DURATION_TOKENS: Array<DurationTokenType> = [
    {
        key: "motionPage.durationShort",
        name: "short"
    },
    {
        key: "motionPage.durationMedium",
        name: "medium"
    },
    {
        key: "motionPage.durationLong",
        name: "long"
    },
    {
        key: "motionPage.durationStagger",
        name: "stagger"
    },
    {
        key: "motionPage.durationSpin",
        name: "spin"
    },
    {
        key: "motionPage.durationLoop",
        name: "loop"
    },
    {
        key: "motionPage.durationBreathe",
        name: "breathe"
    },
    {
        key: "motionPage.durationBlink",
        name: "blink"
    }
];

const EASING_CURVES: Array<EasingCurveType> = [
    {
        descriptionKey: "motionPage.curveStandard",
        name: "standard",
        bezier: [
            0.25,
            0.1,
            0.25,
            1
        ]
    },
    {
        descriptionKey: "motionPage.curveEnter",
        name: "enter",
        bezier: [
            0,
            0,
            0.58,
            1
        ]
    },
    {
        descriptionKey: "motionPage.curveExit",
        name: "exit",
        bezier: [
            0.42,
            0,
            1,
            1
        ]
    },
    {
        descriptionKey: "motionPage.curveLinear",
        name: "linear",
        bezier: [
            0,
            0,
            1,
            1
        ]
    },
    {
        descriptionKey: "motionPage.curveInOut",
        name: "inOut",
        bezier: [
            0.42,
            0,
            0.58,
            1
        ]
    }
];

const INTENT_PURPOSES: Array<IntentPurposeType> = [
    {
        key: "motionPage.purposeHover",
        intent: "hover"
    },
    {
        key: "motionPage.purposePress",
        intent: "press"
    },
    {
        key: "motionPage.purposeToggle",
        intent: "toggle"
    },
    {
        key: "motionPage.purposeIconSwap",
        intent: "iconSwap"
    },
    {
        key: "motionPage.purposeIconAppear",
        intent: "iconAppear"
    },
    {
        key: "motionPage.purposeSurfaceEnter",
        intent: "surfaceEnter"
    },
    {
        key: "motionPage.purposeSurfaceExit",
        intent: "surfaceExit"
    },
    {
        key: "motionPage.purposePanelEnter",
        intent: "panelEnter"
    },
    {
        key: "motionPage.purposePanelExit",
        intent: "panelExit"
    },
    {
        key: "motionPage.purposeExpand",
        intent: "expand"
    },
    {
        key: "motionPage.purposeCollapse",
        intent: "collapse"
    },
    {
        key: "motionPage.purposeListInsert",
        intent: "listInsert"
    },
    {
        key: "motionPage.purposeListRemove",
        intent: "listRemove"
    },
    {
        key: "motionPage.purposeContentSwap",
        intent: "contentSwap"
    },
    {
        key: "motionPage.purposeValueChange",
        intent: "valueChange"
    },
    {
        key: "motionPage.purposeStack",
        intent: "stack"
    },
    {
        key: "motionPage.purposeChartEnter",
        intent: "chartEnter"
    },
    {
        key: "motionPage.purposeChartDraw",
        intent: "chartDraw"
    }
];

const INTENT_ROWS: Array<IntentRowType> = [
    {
        reduceKey: "motionPage.reduceSame",
        moveKey: "motionPage.moveHover",
        where: "StateLayer",
        intents: [
            "hover"
        ]
    },
    {
        reduceKey: "motionPage.reduceNoScale",
        whereKey: "motionPage.wherePress",
        moveKey: "motionPage.movePress",
        moveArgs: [
            MOTION_PRESS_SCALE
        ],
        intents: [
            "press"
        ]
    },
    {
        reduceKey: "motionPage.reduceSameFocus",
        whereKey: "motionPage.whereFocus",
        moveKey: "motionPage.moveFocus",
        time: "0 ms",
        intents: [
            "focus"
        ]
    },
    {
        reduceKey: "motionPage.reduceMoveInstant",
        where: "RadioButton, Switch, TabBar",
        moveKey: "motionPage.moveToggle",
        intents: [
            "toggle"
        ]
    },
    {
        reduceKey: "motionPage.reduceMoveInstant",
        whereKey: "motionPage.whereIconSwap",
        moveKey: "motionPage.moveIconSwap",
        intents: [
            "iconSwap"
        ]
    },
    {
        whereKey: "motionPage.whereIconAppear",
        moveKey: "motionPage.moveIconAppear",
        reduceKey: "motionPage.reduceFade",
        moveArgs: [
            MOTION_ICON_APPEAR_SCALE,
            MOTION_ICON_APPEAR_BLUR
        ],
        intents: [
            "iconAppear"
        ]
    },
    {
        whereKey: "motionPage.whereIconSignature",
        moveKey: "motionPage.moveIconSignature",
        reduceKey: "motionPage.reduceOff",
        timeKey: "motionPage.timePerIcon",
        intents: [
            "iconSignature"
        ]
    },
    {
        where: "Popover, DropdownMenu, ContextMenu, Menubar, HoverCard, Tooltip, SelectBox, Combobox, Command",
        reduceKey: "motionPage.reduceFade",
        moveKey: "motionPage.moveSurface",
        moveArgs: [
            MOTION_SURFACE_SCALE
        ],
        intents: [
            "surfaceEnter",
            "surfaceExit"
        ]
    },
    {
        reduceKey: "motionPage.reduceFade",
        whereKey: "motionPage.wherePanel",
        moveKey: "motionPage.movePanel",
        moveArgs: [
            MOTION_SURFACE_SCALE
        ],
        intents: [
            "panelEnter",
            "panelExit"
        ]
    },
    {
        where: "Accordion, Collapsible, TreeView, Sidebar, EmbeddedMenu, DataTable",
        reduceKey: "motionPage.reduceMoveInstant",
        moveKey: "motionPage.moveExpand",
        intents: [
            "expand",
            "collapse"
        ]
    },
    {
        where: "Attachment, FileUpload, Combobox, DataTable, SwipeableRow, Toast",
        reduceKey: "motionPage.reduceFade",
        moveKey: "motionPage.moveList",
        intents: [
            "listInsert",
            "listRemove"
        ]
    },
    {
        reduceKey: "motionPage.reduceMoveInstant",
        whereKey: "motionPage.whereSwap",
        moveKey: "motionPage.moveSwap",
        intents: [
            "contentSwap"
        ]
    },
    {
        where: "BarChart, PieChart, RadarChart, RadialChart, Sparkline",
        moveKey: "motionPage.moveChartEnter",
        reduceKey: "motionPage.reduceFade",
        intents: [
            "chartEnter"
        ],
        moveDurations: [
            "stagger"
        ]
    },
    {
        where: "LineChart, AreaChart, Sparkline",
        moveKey: "motionPage.moveChartDraw",
        reduceKey: "motionPage.reduceFade",
        intents: [
            "chartDraw"
        ]
    },
    {
        moveKey: "motionPage.moveChartUpdate",
        whereKey: "motionPage.whereChartAll",
        reduceKey: "motionPage.reduceFade",
        intents: [
            "valueChange",
            "listInsert",
            "listRemove",
            "surfaceEnter",
            "surfaceExit"
        ]
    },
    {
        where: "Badge.Indicator, StatTile, Progress, Stepper",
        reduceKey: "motionPage.reduceInstant",
        moveKey: "motionPage.moveValue",
        intents: [
            "valueChange"
        ]
    },
    {
        reduceKey: "motionPage.reduceInstant",
        moveKey: "motionPage.moveStack",
        where: "Toast",
        intents: [
            "stack"
        ]
    },
    {
        where: "Loading, Skeleton, Progress, Trace",
        reduceKey: "motionPage.reduceLoadingRow",
        moveKey: "motionPage.moveLoading",
        time: "spin, loop · linear",
        intents: [
            "loading"
        ]
    },
    {
        whereKey: "motionPage.whereAttention",
        reduceKey: "motionPage.reduceStatic",
        moveKey: "motionPage.moveAttention",
        intents: [
            "attention"
        ],
        time: "inOut"
    },
    {
        where: "Toast, SwipeableRow, Sheet, BottomSheet, Carousel",
        reduceKey: "motionPage.reduceInstant",
        moveKey: "motionPage.moveDismiss",
        intents: [
            "dismissSwipe"
        ],
        time: "spring"
    }
];

const RULE_COUNT = 13;

const CHART_STAGGER_STEPS = 8;

const NOTICE_MAXIMUM_RATIO = 2;

const MS_PER_SECOND = 1000;

const HELPER_ROWS: Array<HelperRowType> = [
    {
        textKey: "motionPage.helperIntents",
        kindKey: "motionPage.kindConstant",
        names: [
            "MOTION_INTENTS"
        ]
    },
    {
        kindKey: "motionPage.kindFunction",
        textKey: "motionPage.helperTiming",
        names: [
            "getMotionTiming"
        ]
    },
    {
        textKey: "motionPage.helperDuration",
        kindKey: "motionPage.kindFunction",
        names: [
            "getMotionDuration"
        ]
    },
    {
        textKey: "motionPage.helperMotionTiming",
        kindKey: "motionPage.kindHook",
        names: [
            "useMotionTiming"
        ]
    },
    {
        textKey: "motionPage.helperAnimatedState",
        kindKey: "motionPage.kindHook",
        names: [
            "useAnimatedState"
        ]
    },
    {
        textKey: "motionPage.helperPressScale",
        kindKey: "motionPage.kindHook",
        names: [
            "usePressScale"
        ]
    },
    {
        textKey: "motionPage.helperPresence",
        kindKey: "motionPage.kindHook",
        names: [
            "usePresence"
        ]
    },
    {
        kindKey: "motionPage.kindFunction",
        textKey: "motionPage.helperAppear",
        names: [
            "getAppearStyle"
        ]
    },
    {
        kindKey: "motionPage.kindFunction",
        textKey: "motionPage.helperSlide",
        names: [
            "getSlideStyle"
        ]
    },
    {
        textKey: "motionPage.helperCrossfade",
        kindKey: "motionPage.kindFunction",
        names: [
            "getCrossfadeStyle"
        ]
    },
    {
        textKey: "motionPage.helperTransition",
        kindKey: "motionPage.kindFunction",
        names: [
            "getMotionTransition"
        ]
    },
    {
        kindKey: "motionPage.kindConstant",
        textKey: "motionPage.helperTokens",
        names: [
            "MOTION_EASINGS",
            "MOTION_SPRINGS"
        ]
    },
    {
        textKey: "motionPage.helperReduced",
        kindKey: "motionPage.kindHook",
        names: [
            "useReducedMotion"
        ]
    },
    {
        textKey: "motionPage.helperSuppress",
        kindKey: "motionPage.kindFunction",
        names: [
            "suppressWebTransitions"
        ]
    }
];

const MORPH_ICON_PAIRS: Array<[LucideIcon, LucideIcon]> = [
    [
        ChevronRightIcon,
        ChevronDownIcon
    ],
    [
        ChevronDownIcon,
        ChevronUpIcon
    ],
    [
        EyeIcon,
        EyeClosedIcon
    ],
    [
        EyeIcon,
        EyeOffIcon
    ],
    [
        SunIcon,
        MoonIcon
    ],
    [
        ArrowUpDownIcon,
        ArrowDownIcon
    ],
    [
        ArrowUpDownIcon,
        ArrowUpIcon
    ],
    [
        ArrowUpIcon,
        ArrowDownIcon
    ],
    [
        CheckIcon,
        CheckCheckIcon
    ],
    [
        CopyIcon,
        CheckIcon
    ],
    [
        MenuIcon,
        XIcon
    ],
    [
        PlusIcon,
        MinusIcon
    ],
    [
        PlusIcon,
        XIcon
    ],
    [
        Volume2Icon,
        VolumeXIcon
    ],
    [
        BookmarkIcon,
        BookmarkCheckIcon
    ],
    [
        BellIcon,
        BellOffIcon
    ],
    [
        MicIcon,
        MicOffIcon
    ],
    [
        WifiIcon,
        WifiOffIcon
    ],
    [
        LockIcon,
        LockOpenIcon
    ]
];

const APPEAR_TOPICS = [
    {
        key: "motionPage.appearDesign",
        id: "design"
    },
    {
        key: "motionPage.appearCode",
        id: "code"
    },
    {
        key: "motionPage.appearMotion",
        id: "motion"
    }
];

const STICKER_STATUSES = [
    {
        key: "motionPage.statusDraft",
        type: "neutral"
    },
    {
        key: "motionPage.statusReview",
        type: "info"
    },
    {
        key: "motionPage.statusApproved",
        type: "success"
    },
    {
        key: "motionPage.statusRejected",
        type: "danger"
    },
    {
        key: "motionPage.statusPaused",
        type: "warning"
    }
] as const;

const INITIAL_STICKERS: Array<StickerItemType> = [
    {
        isVisible: true,
        isAdded: false,
        statusIndex: 0,
        id: 0
    },
    {
        isVisible: true,
        isAdded: false,
        statusIndex: 1,
        id: 1
    },
    {
        isVisible: true,
        isAdded: false,
        statusIndex: 2,
        id: 2
    }
];

const MAX_VISIBLE_STICKERS = 8;

const SIGNATURE_BUTTONS = [
    {
        labelKey: "motionPage.iconClose",
        icon: XIcon
    },
    {
        labelKey: "motionPage.iconCopy",
        icon: CopyIcon
    },
    {
        labelKey: "motionPage.iconDelete",
        icon: Trash2Icon
    },
    {
        labelKey: "motionPage.iconSearch",
        icon: SearchIcon
    },
    {
        labelKey: "motionPage.iconSettings",
        icon: SettingsIcon
    },
    {
        labelKey: "motionPage.iconNotifications",
        icon: BellIcon
    }
];

const GRAPH_SIZE = 96;

const GRAPH_PADDING = 8;

const GRAPH_SPAN = GRAPH_SIZE - GRAPH_PADDING * 2;

const SPRING_GRAPH_WIDTH = 264;

const SPRING_GRAPH_HEIGHT = 136;

const SPRING_GRAPH_PADDING = 14;

const SPRING_GRAPH_DURATION = 0.6;

const SPRING_GRAPH_CEILING = 1.3;

const SPRING_INTEGRATION_STEP = 0.0005;

const SPRING_SAMPLE_EVERY = 10;

const SPRING_SETTLE_TOLERANCE = 0.02;

const SPRING_COMPARISON_RATIO = 0.5;

const getSpringSamples = ({
    stiffness,
    damping,
    mass
}: MotionSpring) => {
    const stepCount = Math.round(SPRING_GRAPH_DURATION / SPRING_INTEGRATION_STEP);

    const samples = [
        0
    ];

    let position = 0;

    let velocity = 0;

    for(let step = 1; step <= stepCount; step += 1) {
        const acceleration = (stiffness * (1 - position) - damping * velocity) / mass;

        velocity += acceleration * SPRING_INTEGRATION_STEP;

        position += velocity * SPRING_INTEGRATION_STEP;

        if(step % SPRING_SAMPLE_EVERY === 0) {
            samples.push(position);
        }
    }

    return samples;
};

const getSpringSettleTime = (samples: Array<number>) => {
    let settleIndex = 0;

    samples.forEach((value, index) => {
        if(Math.abs(1 - value) > SPRING_SETTLE_TOLERANCE) {
            settleIndex = index + 1;
        }
    });

    const settleSeconds = settleIndex * SPRING_SAMPLE_EVERY * SPRING_INTEGRATION_STEP;

    return Math.round(settleSeconds * 100) * 10;
};

const getSpringX = (index: number, count: number) => SPRING_GRAPH_PADDING + index / (count - 1) * (SPRING_GRAPH_WIDTH - SPRING_GRAPH_PADDING * 2);

const getSpringY = (value: number) => SPRING_GRAPH_HEIGHT - SPRING_GRAPH_PADDING - Math.min(value, SPRING_GRAPH_CEILING) / SPRING_GRAPH_CEILING * (SPRING_GRAPH_HEIGHT - SPRING_GRAPH_PADDING * 2);

const getSpringPath = (samples: Array<number>) => samples.map((value, index) => `${index === 0 ? "M" : "L"} ${getSpringX(index, samples.length).toFixed(1)} ${getSpringY(value).toFixed(1)}`).join(" ");

const getGraphX = (value: number) => GRAPH_PADDING + value * GRAPH_SPAN;

const getGraphY = (value: number) => GRAPH_PADDING + (1 - value) * GRAPH_SPAN;

const getIconName = (icon: LucideIcon) => (icon.displayName ?? "").replace(/([a-z])([A-Z0-9])/g, "$1-$2").toLowerCase();

const isMotionIntent = (intent: string): intent is MotionIntentType => intent in MOTION_INTENTS;

const formatDecimal = (value: number, locale: string) => new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2
}).format(value);

const createSignatureIcon = (icon: LucideIcon): NCoreUIKitIcon => {
    const SignatureIcon: NCoreUIKitIcon = ({
        customColor,
        size
    }) => <AnimatedIcon
        customColor={customColor}
        animationTrigger="hover"
        animation="signature"
        icon={icon}
        size={size}
    />;

    return SignatureIcon;
};

const ClearIcon = createSignatureIcon(XIcon);

const SIGNATURE_ICONS = SIGNATURE_BUTTONS.map(({
    labelKey,
    icon
}) => ({
    icon: createSignatureIcon(icon),
    labelKey
}));

const MORPH_PAIRS: Array<MorphPairType> = MORPH_ICON_PAIRS.map(([
    fromIcon,
    toIcon
]) => ({
    ...createTransitionIcon(fromIcon, toIcon),
    label: `${getIconName(fromIcon)} ↔ ${getIconName(toIcon)}`,
    fromName: getIconName(fromIcon),
    toName: getIconName(toIcon)
}));

const SPRING_SAMPLES = getSpringSamples(MOTION_SPRINGS.standard);

const SPRING_COMPARISON_SAMPLES = getSpringSamples({
    ...MOTION_SPRINGS.standard,
    damping: 2 * SPRING_COMPARISON_RATIO * Math.sqrt(MOTION_SPRINGS.standard.stiffness * MOTION_SPRINGS.standard.mass)
});

const SPRING_RATIO = MOTION_SPRINGS.standard.damping / (2 * Math.sqrt(MOTION_SPRINGS.standard.stiffness * MOTION_SPRINGS.standard.mass));

const SPRING_SETTLE_MS = getSpringSettleTime(SPRING_SAMPLES);

const useMotionStyles = () => {
    const {
        controlHeights,
        shapeConfigs,
        avatarSizes,
        radiuses,
        borders,
        spaces
    } = NCoreUIKitTheme.useContext();

    return useMemo(() => ({
        grid: {
            gridTemplateColumns: `repeat(auto-fit, minmax(${shapeConfigs.fieldMinWidth}px, 1fr))`,
            gap: spaces.spacingMd,
            display: "grid",
            width: "100%"
        },
        morphGrid: {
            gridTemplateColumns: `repeat(auto-fill, minmax(${avatarSizes.xLarge}px, 1fr))`,
            gap: `${spaces.spacingLg}px ${spaces.spacingMd}px`,
            display: "grid",
            width: "100%"
        },
        stage: {
            width: shapeConfigs.sideListWidth,
            height: controlHeights.medium,
            justifyContent: "center",
            alignItems: "center",
            display: "flex"
        },
        springGraph: {
            width: shapeConfigs.drawerWidth,
            maxWidth: "100%"
        },
        springTrack: {
            width: shapeConfigs.drawerWidth,
            height: controlHeights.large,
            borderRadius: radiuses.full,
            justifyContent: "center",
            alignItems: "center"
        },
        stickerRow: {
            width: shapeConfigs.drawerWidth,
            minHeight: avatarSizes.large,
            justifyContent: "center",
            alignContent: "center",
            gap: spaces.spacingSm,
            alignItems: "center",
            flexWrap: "wrap",
            maxWidth: "100%",
            display: "flex"
        },
        swapWidth: {
            width: shapeConfigs.drawerWidth,
            maxWidth: "100%"
        },
        slot: {
            height: controlHeights.medium,
            width: controlHeights.medium,
            justifyContent: "center",
            alignItems: "center",
            display: "flex"
        },
        springHandle: {
            height: controlHeights.medium,
            borderRadius: radiuses.full,
            width: controlHeights.medium
        },
        curveDot: {
            borderRadius: radiuses.full,
            height: spaces.spacingMd,
            width: spaces.spacingMd
        },
        curveRail: {
            borderRadius: radiuses.full,
            height: borders.chartLine,
            position: "absolute",
            right: 0,
            left: 0
        },
        swapStage: {
            height: avatarSizes.xLarge,
            position: "relative",
            width: "100%"
        },
        curveTrack: {
            width: avatarSizes.xLarge,
            justifyContent: "center",
            height: spaces.spacingMd
        },
        group: {
            alignItems: "flex-start",
            flexDirection: "column",
            gap: spaces.spacingMd,
            display: "flex"
        },
        intent: {
            alignItems: "flex-start",
            flexDirection: "column",
            gap: spaces.spacingMd,
            whiteSpace: "nowrap",
            display: "flex"
        },
        intentItem: {
            alignItems: "flex-start",
            flexDirection: "column",
            gap: spaces.spacingXxs,
            display: "flex"
        },
        row: {
            justifyContent: "center",
            gap: spaces.spacingMd,
            alignItems: "center",
            flexWrap: "wrap",
            display: "flex"
        },
        stack: {
            alignItems: "flex-start",
            flexDirection: "column",
            gap: spaces.spacingSm,
            display: "flex"
        },
        column: {
            flexDirection: "column",
            gap: spaces.spacingMd,
            alignItems: "center",
            maxWidth: "100%",
            display: "flex"
        },
        morphCell: {
            flexDirection: "column",
            gap: spaces.spacingSm,
            alignItems: "center",
            display: "flex"
        },
        morphName: {
            flexDirection: "column",
            alignItems: "center",
            display: "flex"
        },
        status: {
            gap: spaces.spacingMd,
            alignItems: "center",
            display: "flex"
        }
    }) as const, [
        controlHeights,
        shapeConfigs,
        avatarSizes,
        radiuses,
        borders,
        spaces
    ]);
};

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const stylesheet = StyleSheet.create({
    springAxis: {
        justifyContent: "space-between",
        flexDirection: "row"
    },
    swapPanel: {
        position: "absolute",
        right: 0,
        left: 0,
        top: 0
    },
    curveCard: {
        alignItems: "center"
    },
    label: {
        textAlign: "center"
    },
    springHandle: {
        ...webStyle({
            cursor: "grab"
        })
    }
});

const MotionTable = ({
    headers,
    rows
}: {
    rows: Array<Array<ReactNode>>;
    headers: Array<string>;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <DocsTable
        rows={rows.map((row) => row.map((cell) => <View
            style={{
                paddingVertical: spaces.spacingSm
            }}
        >
            {
                typeof cell === "string" ?
                    <Text
                        variant="bodyMediumSize"
                        color="high"
                    >
                        {cell}
                    </Text>
                :
                    cell
            }
        </View>))}
        headers={headers}
    />;
};

const RuleList = ({
    items
}: {
    items: Array<string>;
}) => <BulletList
    isOrdered={true}
    items={items}
/>;

const CurveGraph = ({
    bezier,
    label
}: {
    bezier: BezierType;
    label: string;
}) => {
    const [
        x1,
        y1,
        x2,
        y2
    ] = bezier;

    const {
        avatarSizes,
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const dash = `${spaces.spacingXs} ${spaces.spacingXs}`;

    return <svg
        viewBox={`0 0 ${GRAPH_SIZE} ${GRAPH_SIZE}`}
        height={avatarSizes.xLarge}
        width={avatarSizes.xLarge}
        aria-label={label}
        role="img"
    >
        <rect
            stroke={colors.content.border.subtle}
            strokeWidth={borders.line}
            height={GRAPH_SPAN}
            width={GRAPH_SPAN}
            x={GRAPH_PADDING}
            y={GRAPH_PADDING}
            fill="none"
        />
        <line
            stroke={colors.content.border.default}
            strokeWidth={borders.line}
            strokeDasharray={dash}
            y2={getGraphY(1)}
            y1={getGraphY(0)}
            x1={getGraphX(0)}
            x2={getGraphX(1)}
        />
        <line
            stroke={colors.content.text.low}
            strokeWidth={borders.line}
            x2={getGraphX(x1)}
            y2={getGraphY(y1)}
            y1={getGraphY(0)}
            x1={getGraphX(0)}
        />
        <line
            stroke={colors.content.text.low}
            strokeWidth={borders.line}
            x2={getGraphX(x2)}
            y2={getGraphY(y2)}
            y1={getGraphY(1)}
            x1={getGraphX(1)}
        />
        <path
            d={`M ${getGraphX(0)} ${getGraphY(0)} C ${getGraphX(x1)} ${getGraphY(y1)}, ${getGraphX(x2)} ${getGraphY(y2)}, ${getGraphX(1)} ${getGraphY(1)}`}
            stroke={colors.content.text.high}
            strokeWidth={borders.halo}
            strokeLinecap="round"
            fill="none"
        />
        <circle
            fill={colors.content.container.default}
            stroke={colors.content.text.low}
            strokeWidth={borders.line}
            r={spaces.spacingXs}
            cy={getGraphY(y1)}
            cx={getGraphX(x1)}
        />
        <circle
            fill={colors.content.container.default}
            stroke={colors.content.text.low}
            strokeWidth={borders.line}
            r={spaces.spacingXs}
            cy={getGraphY(y2)}
            cx={getGraphX(x2)}
        />
    </svg>;
};

const CurveCard = ({
    progress,
    curve
}: {
    progress: Animated.Value;
    curve: EasingCurveType;
}) => {
    const {
        t
    } = useDocsI18n();

    const styles = useMotionStyles();

    const {
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const translateX = progress.interpolate({
        outputRange: [
            0,
            styles.curveTrack.width - styles.curveDot.width
        ],
        inputRange: [
            0,
            1
        ]
    });

    const curveIntents = Object.keys(MOTION_INTENTS).filter(isMotionIntent).filter((intent) => MOTION_INTENTS[intent].easing === curve.name);

    return <Card
        spreadBehaviour="stretch"
        variant="filled"
    >
        <Card.Content>
            <View
                style={[
                    stylesheet.curveCard,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <CurveGraph
                    label={t("motionPage.curveLabel", [
                        curve.name
                    ])}
                    bezier={curve.bezier}
                />
                <Text
                    variant="monoMediumSize"
                    color="high"
                >
                    {curve.name}
                </Text>
                <Text
                    style={stylesheet.label}
                    variant="bodySmallSize"
                    color="low"
                >
                    {`${MOTION_CSS_EASINGS[curve.name]} · ${curve.bezier.join(", ")}`}
                </Text>
                <Text
                    style={stylesheet.label}
                    variant="bodySmallSize"
                    color="mid"
                >
                    {t(curve.descriptionKey)}
                </Text>
                {
                    curveIntents.length ?
                        <Text
                            style={stylesheet.label}
                            variant="bodySmallSize"
                            color="low"
                        >
                            {t("motionPage.curveIntents", [
                                curveIntents.join(", ")
                            ])}
                        </Text>
                    :
                        null
                }
                <View
                    style={styles.curveTrack}
                    aria-hidden={true}
                >
                    <View
                        style={[
                            styles.curveRail,
                            {
                                backgroundColor: colors.content.border.subtle
                            }
                        ]}
                    />
                    <Animated.View
                        style={[
                            styles.curveDot,
                            {
                                backgroundColor: colors.content.text.high,
                                transform: [
                                    {
                                        translateX
                                    }
                                ]
                            }
                        ]}
                    />
                </View>
            </View>
        </Card.Content>
    </Card>;
};

const EasingsPreview = () => {
    const {
        t
    } = useDocsI18n();

    const styles = useMotionStyles();

    const {
        durations
    } = NCoreUIKitTheme.useContext();

    const isReduceMotionEnabled = useReducedMotion();

    const tracks = useRef(EASING_CURVES.map((curve) => ({
        progress: new Animated.Value(0),
        curve
    }))).current;

    const handleReplay = () => {
        const duration = getMotionDuration({
            isReduceMotionEnabled,
            duration: "long",
            durations
        });

        tracks.forEach(({
            progress
        }) => progress.setValue(0));

        Animated.parallel(tracks.map(({
            progress,
            curve
        }) => Animated.timing(progress, {
            useNativeDriver: Platform.OS !== "web",
            easing: MOTION_EASINGS[curve.name],
            toValue: 1,
            duration
        }))).start();
    };

    return <Preview
        caption={t("motionPage.curvesCaption", [
            durations.long
        ])}
    >
        <div
            style={styles.grid}
        >
            {
                tracks.map(({
                    progress,
                    curve
                }) => <CurveCard
                    progress={progress}
                    key={curve.name}
                    curve={curve}
                />)
            }
        </div>
        <Button
            title={t("motionPage.replay")}
            onPress={handleReplay}
            spreadBehaviour="free"
            variant="outline"
        />
    </Preview>;
};

const SpringGraph = () => {
    const {
        t
    } = useDocsI18n();

    const baselineY = getSpringY(1);

    const bottomY = getSpringY(0);

    const styles = useMotionStyles();

    const {
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    return <View
        style={styles.springGraph}
    >
        <svg
            viewBox={`0 0 ${SPRING_GRAPH_WIDTH} ${SPRING_GRAPH_HEIGHT}`}
            aria-label={t("motionPage.springLabel")}
            width="100%"
            role="img"
        >
            <line
                x2={SPRING_GRAPH_WIDTH - SPRING_GRAPH_PADDING}
                stroke={colors.content.border.subtle}
                strokeWidth={borders.line}
                x1={SPRING_GRAPH_PADDING}
                y2={bottomY}
                y1={bottomY}
            />
            <line
                strokeDasharray={`${spaces.spacingXs} ${spaces.spacingXs}`}
                x2={SPRING_GRAPH_WIDTH - SPRING_GRAPH_PADDING}
                stroke={colors.content.border.default}
                strokeWidth={borders.line}
                x1={SPRING_GRAPH_PADDING}
                y2={baselineY}
                y1={baselineY}
            />
            <path
                strokeDasharray={`${spaces.spacingXs} ${spaces.spacingXs}`}
                d={getSpringPath(SPRING_COMPARISON_SAMPLES)}
                stroke={colors.content.text.low}
                strokeWidth={borders.chartLine}
                strokeLinejoin="round"
                strokeLinecap="round"
                fill="none"
            />
            <path
                d={getSpringPath(SPRING_SAMPLES)}
                stroke={colors.content.text.high}
                strokeWidth={borders.halo}
                strokeLinejoin="round"
                strokeLinecap="round"
                fill="none"
            />
        </svg>
        <View
            style={[
                stylesheet.springAxis,
                {
                    paddingHorizontal: `${SPRING_GRAPH_PADDING / SPRING_GRAPH_WIDTH * 100}%`
                }
            ]}
        >
            <Text
                variant="bodySmallSize"
                color="low"
            >
                0
            </Text>
            <Text
                variant="bodySmallSize"
                color="low"
            >
                {`${SPRING_GRAPH_DURATION * 1000} ms`}
            </Text>
        </View>
    </View>;
};

const SpringDemo = () => {
    const styles = useMotionStyles();

    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const springLimit = (styles.springTrack.width - styles.springHandle.width) / 2;

    const isReduceMotionEnabled = useReducedMotion();

    const isReduceMotionRef = useRef(isReduceMotionEnabled);

    const translateX = useRef(new Animated.Value(0)).current;

    isReduceMotionRef.current = isReduceMotionEnabled;

    const handleRelease = (_event: unknown, gesture: PanResponderGestureState) => {
        translateX.flattenOffset();

        if(isReduceMotionRef.current) {
            translateX.setValue(0);

            return;
        }

        Animated.spring(translateX, {
            ...MOTION_SPRINGS.standard,
            useNativeDriver: Platform.OS !== "web",
            velocity: gesture.vx,
            toValue: 0
        }).start();
    };

    const panResponder = useMemo(() => PanResponder.create({
        onPanResponderMove: (_event, gesture) => {
            translateX.setValue(gesture.dx);
        },
        onPanResponderGrant: () => {
            translateX.stopAnimation((value) => {
                translateX.setOffset(value);

                translateX.setValue(0);
            });
        },
        onStartShouldSetPanResponder: () => true,
        onPanResponderTerminate: handleRelease,
        onPanResponderRelease: handleRelease
    }), []);

    const clampedX = translateX.interpolate({
        extrapolate: "clamp",
        inputRange: [
            -springLimit,
            springLimit
        ],
        outputRange: [
            -springLimit,
            springLimit
        ]
    });

    return <View
        style={[
            styles.springTrack,
            {
                backgroundColor: colors.content.container.subtle
            }
        ]}
        aria-hidden={true}
    >
        <Animated.View
            {...panResponder.panHandlers}
            style={[
                styles.springHandle,
                stylesheet.springHandle,
                {
                    backgroundColor: colors.content.text.high,
                    transform: [
                        {
                            translateX: clampedX
                        }
                    ]
                }
            ]}
        />
    </View>;
};

const SpringPreview = () => {
    const {
        language,
        t
    } = useDocsI18n();

    return <Preview
        caption={t("motionPage.springCaption", [
            formatDecimal(SPRING_COMPARISON_RATIO, getKitLocale(language))
        ])}
    >
        <SpringGraph/>
        <SpringDemo/>
    </Preview>;
};

const PressControl = ({
    title
}: {
    title: string;
}) => {
    const {
        shapeConfigs,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        interactionHandlers,
        isFocusVisible,
        isPressed
    } = useInteractionState();

    const pressScaleStyle = usePressScale({
        isPressed
    });

    return <AnimatedPressable
        {...interactionHandlers}
        style={[
            {
                ...getCornerStyle({
                    radius: radiuses.chip,
                    shapeConfigs
                })
            },
            getFocusRingStyle({
                isFocusVisible,
                borders,
                colors
            }),
            pressScaleStyle
        ]}
        accessibilityLabel={title}
        accessibilityRole="button"
        onPress={() => undefined}
    >
        <Badge
            variant="outline"
            type="neutral"
            title={title}
        />
    </AnimatedPressable>;
};

const PressDemo = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isChipSelected,
        setIsChipSelected
    ] = useState(false);

    return <Preview
        caption={t("motionPage.pressCaption")}
    >
        <Button
            title={t("motionPage.pressSave")}
            onPress={() => undefined}
            spreadBehaviour="free"
        />
        <Button
            title={t("motionPage.pressShare")}
            onPress={() => undefined}
            spreadBehaviour="free"
            variant="tonal"
        />
        <Button
            title={t("motionPage.pressCancel")}
            onPress={() => undefined}
            spreadBehaviour="free"
            variant="outline"
        />
        <Chip
            onPress={() => setIsChipSelected(!isChipSelected)}
            title={t("motionPage.pressChip")}
            isSelected={isChipSelected}
            spreadBehaviour="free"
        />
        <PressControl
            title="usePressScale"
        />
    </Preview>;
};

const ToggleDemo = () => {
    const {
        t
    } = useDocsI18n();

    const styles = useMotionStyles();

    const [
        isNotified,
        setIsNotified
    ] = useState(true);

    const [
        frequency,
        setFrequency
    ] = useState("weekly");

    const [
        isAutoSaved,
        setIsAutoSaved
    ] = useState(false);

    return <Preview
        caption={t("motionPage.toggleCaption")}
    >
        <div
            style={styles.group}
        >
            <CheckBox
                title={t("motionPage.toggleCheck")}
                onCheckedChange={setIsNotified}
                isChecked={isNotified}
            />
            <Switch
                title={t("motionPage.toggleSwitch")}
                onCheckedChange={setIsAutoSaved}
                isChecked={isAutoSaved}
            />
        </div>
        <div
            aria-label={t("motionPage.toggleWeekly")}
            style={styles.group}
            role="radiogroup"
        >
            <RadioButton
                onPress={() => setFrequency("weekly")}
                title={t("motionPage.toggleWeekly")}
                isChecked={frequency === "weekly"}
            />
            <RadioButton
                onPress={() => setFrequency("monthly")}
                title={t("motionPage.toggleMonthly")}
                isChecked={frequency === "monthly"}
            />
        </div>
    </Preview>;
};

const AppearDemo = () => {
    const {
        t
    } = useDocsI18n();

    const styles = useMotionStyles();

    const [
        selected,
        setSelected
    ] = useState<Array<string>>([]);

    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        isVisible: selected.length > 0,
        enterIntent: "iconAppear",
        exitIntent: "iconAppear"
    });

    const handleSelect = (id: string) => {
        setSelected(selected.includes(id) ? selected.filter((item) => item !== id) : [
            ...selected,
            id
        ]);
    };

    return <Preview
        caption={t("motionPage.appearCaption")}
    >
        <div
            style={styles.row}
        >
            {
                APPEAR_TOPICS.map((topic) => <Chip
                    isSelected={selected.includes(topic.id)}
                    onPress={() => handleSelect(topic.id)}
                    spreadBehaviour="free"
                    title={t(topic.key)}
                    key={topic.id}
                />)
            }
            <div
                style={styles.slot}
            >
                {
                    isRendered ?
                        <Animated.View
                            style={getAppearStyle({
                                isReduceMotionEnabled,
                                progress
                            })}
                        >
                            <Button
                                accessibilityLabel={t("motionPage.appearClear")}
                                onPress={() => setSelected([])}
                                icon={ClearIcon}
                                variant="ghost"
                                type="neutral"
                                size="small"
                            />
                        </Animated.View>
                    :
                        null
                }
            </div>
        </div>
    </Preview>;
};

const PresenceSticker = ({
    onExited,
    item
}: {
    onExited: (id: number) => void;
    item: StickerItemType;
}) => {
    const {
        t
    } = useDocsI18n();

    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        onExited: () => onExited(item.id),
        isInitialAnimated: item.isAdded,
        enterIntent: "listInsert",
        isVisible: item.isVisible,
        exitIntent: "listRemove"
    });

    const status = STICKER_STATUSES[item.statusIndex % STICKER_STATUSES.length] ?? STICKER_STATUSES[0];

    if(!isRendered) {
        return null;
    }

    return <Animated.View
        style={getSlideStyle({
            offset: spaces.spacingSm,
            isReduceMotionEnabled,
            progress
        })}
    >
        <Badge
            spreadBehaviour="free"
            title={t(status.key)}
            type={status.type}
        />
    </Animated.View>;
};

const ListDemo = () => {
    const {
        t
    } = useDocsI18n();

    const styles = useMotionStyles();

    const [
        stickers,
        setStickers
    ] = useState<Array<StickerItemType>>(INITIAL_STICKERS);

    const nextIdRef = useRef(INITIAL_STICKERS.length);

    const visibleCount = stickers.filter((item) => item.isVisible).length;

    const handleAdd = () => {
        const id = nextIdRef.current;

        nextIdRef.current += 1;

        setStickers((current) => [
            ...current,
            {
                isVisible: true,
                statusIndex: id,
                isAdded: true,
                id
            }
        ]);
    };

    const handleRemove = () => {
        const last = [...stickers].reverse().find((item) => item.isVisible);

        if(!last) {
            return;
        }

        setStickers((current) => current.map((item) => item.id === last.id ? {
            ...item,
            isVisible: false
        } : item));
    };

    const handleExited = (id: number) => {
        setStickers((current) => current.filter((item) => item.id !== id));
    };

    return <Preview
        caption={t("motionPage.listsCaption")}
    >
        <div
            style={styles.column}
        >
            <div
                style={styles.stickerRow}
            >
                {
                    stickers.map((item) => <PresenceSticker
                        onExited={handleExited}
                        key={item.id}
                        item={item}
                    />)
                }
            </div>
            <div
                style={styles.row}
            >
                <Button
                    isDisabled={visibleCount >= MAX_VISIBLE_STICKERS}
                    title={t("motionPage.listsAdd")}
                    spreadBehaviour="free"
                    onPress={handleAdd}
                />
                <Button
                    title={t("motionPage.listsRemove")}
                    isDisabled={visibleCount === 0}
                    onPress={handleRemove}
                    spreadBehaviour="free"
                    variant="outline"
                />
            </div>
        </div>
    </Preview>;
};

const SwapDemo = () => {
    const {
        t
    } = useDocsI18n();

    const styles = useMotionStyles();

    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        panel,
        setPanel
    ] = useState("first");

    const first = usePresence({
        isVisible: panel === "first",
        enterIntent: "contentSwap",
        exitIntent: "contentSwap"
    });

    const second = usePresence({
        isVisible: panel === "second",
        enterIntent: "contentSwap",
        exitIntent: "contentSwap"
    });

    const handleChange = (value: string | Array<string>) => {
        if(typeof value === "string" && value) {
            setPanel(value);
        }
    };

    return <Preview
        caption={t("motionPage.swapCaption")}
    >
        <div
            style={styles.column}
        >
            <ToggleGroup
                title={t("motionPage.swapLabel")}
                onValueChange={handleChange}
                attachBehaviour="attached"
                variant="outline"
                value={panel}
            >
                <ToggleGroup.Item
                    title={t("motionPage.swapSummary")}
                    value="first"
                />
                <ToggleGroup.Item
                    title={t("motionPage.swapDetails")}
                    value="second"
                />
            </ToggleGroup>
            <div
                style={styles.swapWidth}
            >
                <View
                    style={styles.swapStage}
                >
                    {
                        first.isRendered ?
                            <Animated.View
                                style={[
                                    stylesheet.swapPanel,
                                    getSlideStyle({
                                        isReduceMotionEnabled: first.isReduceMotionEnabled,
                                        offset: -spaces.spacingSm,
                                        progress: first.progress
                                    })
                                ]}
                            >
                                <Card
                                    spreadBehaviour="stretch"
                                    variant="outline"
                                >
                                    <Card.Header>
                                        <Card.Title>
                                            {t("motionPage.swapSummary")}
                                        </Card.Title>
                                        <Card.Description>
                                            {t("motionPage.swapSummaryText")}
                                        </Card.Description>
                                    </Card.Header>
                                </Card>
                            </Animated.View>
                        :
                            null
                    }
                    {
                        second.isRendered ?
                            <Animated.View
                                style={[
                                    stylesheet.swapPanel,
                                    getSlideStyle({
                                        isReduceMotionEnabled: second.isReduceMotionEnabled,
                                        offset: spaces.spacingSm,
                                        progress: second.progress
                                    })
                                ]}
                            >
                                <Card
                                    spreadBehaviour="stretch"
                                    variant="outline"
                                >
                                    <Card.Header>
                                        <Card.Title>
                                            {t("motionPage.swapDetails")}
                                        </Card.Title>
                                        <Card.Description>
                                            {t("motionPage.swapDetailsText")}
                                        </Card.Description>
                                    </Card.Header>
                                </Card>
                            </Animated.View>
                        :
                            null
                    }
                </View>
            </div>
        </div>
    </Preview>;
};

const SurfaceDemo = () => {
    const {
        t
    } = useDocsI18n();

    return <Preview
        caption={t("motionPage.surfacesCaption")}
    >
        <Popover>
            <Popover.Trigger>
                <Button
                    title={t("motionPage.surfacesPopover")}
                    onPress={() => undefined}
                    variant="outline"
                    type="neutral"
                />
            </Popover.Trigger>
            <Popover.Content
                description={t("motionPage.surfacesPopoverText")}
                title={t("motionPage.surfacesPopoverTitle")}
            >
                <Popover.Footer>
                    <Popover.Close>
                        <Button
                            title={t("motionPage.surfacesDone")}
                            onPress={() => undefined}
                        />
                    </Popover.Close>
                </Popover.Footer>
            </Popover.Content>
        </Popover>
        <DropdownMenu
            title={t("motionPage.surfacesMenu")}
        >
            <DropdownMenu.Trigger>
                <Button
                    title={t("motionPage.surfacesMenu")}
                    onPress={() => undefined}
                    variant="outline"
                    type="neutral"
                />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
                <DropdownMenu.Item
                    title={t("motionPage.surfacesRename")}
                    onPress={() => undefined}
                />
                <DropdownMenu.Item
                    title={t("motionPage.surfacesDuplicate")}
                    onPress={() => undefined}
                />
                <DropdownMenu.Separator/>
                <DropdownMenu.Item
                    title={t("motionPage.surfacesDelete")}
                    onPress={() => undefined}
                    type="danger"
                />
            </DropdownMenu.Content>
        </DropdownMenu>
    </Preview>;
};

const MorphGallery = () => {
    const {
        t
    } = useDocsI18n();

    const styles = useMotionStyles();

    const [
        states,
        setStates
    ] = useState<Array<boolean>>(() => MORPH_PAIRS.map(() => false));

    const handleToggle = (index: number) => {
        setStates((current) => current.map((state, stateIndex) => stateIndex === index ? !state : state));
    };

    const handleToggleAll = () => {
        const next = !states.every(Boolean);

        setStates(MORPH_PAIRS.map(() => next));
    };

    return <Preview
        caption={t("motionPage.iconsMorphCaption")}
    >
        <Button
            title={t("motionPage.iconsMorphAll")}
            onPress={handleToggleAll}
            spreadBehaviour="free"
            variant="outline"
        />
        <div
            style={styles.morphGrid}
        >
            {
                MORPH_PAIRS.map((pair, index) => <div
                    style={styles.morphCell}
                    key={pair.label}
                >
                    <pair.Provider
                        value={states[index] ?? false}
                    >
                        <Button
                            onPress={() => handleToggle(index)}
                            accessibilityLabel={pair.label}
                            spreadBehaviour="free"
                            variant="outline"
                            icon={pair.Icon}
                            type="neutral"
                        />
                    </pair.Provider>
                    <div
                        style={styles.morphName}
                    >
                        <Text
                            style={stylesheet.label}
                            variant="bodySmallSize"
                            color="low"
                        >
                            {pair.fromName}
                        </Text>
                        <Text
                            style={stylesheet.label}
                            variant="bodySmallSize"
                            color="low"
                        >
                            {`↔ ${pair.toName}`}
                        </Text>
                    </div>
                </div>)
            }
        </div>
    </Preview>;
};

const SignatureDemo = () => {
    const {
        t
    } = useDocsI18n();

    return <Preview
        caption={t("motionPage.iconsSignatureCaption")}
    >
        {
            SIGNATURE_ICONS.map(({
                labelKey,
                icon
            }) => <Button
                accessibilityLabel={t(labelKey)}
                onPress={() => undefined}
                variant="outline"
                type="neutral"
                key={labelKey}
                icon={icon}
            />)
        }
    </Preview>;
};

const ReduceMotionDemo = () => {
    const {
        t
    } = useDocsI18n();

    const styles = useMotionStyles();

    const {
        durations,
        spaces
    } = NCoreUIKitTheme.useContext();

    const isReduceMotionEnabled = useReducedMotion();

    const [
        isShown,
        setIsShown
    ] = useState(true);

    const progress = useAnimatedState({
        value: isShown ? 1 : 0,
        intent: "contentSwap",
        motion: "fade"
    });

    return <Preview
        caption={t("motionPage.reduceCaption", [
            durations.short
        ])}
    >
        <div
            style={styles.column}
        >
            <div
                style={styles.status}
            >
                <Text
                    variant="bodyLargeSize"
                    color="high"
                >
                    {t("motionPage.reduceStatus")}
                </Text>
                <Badge
                    title={isReduceMotionEnabled ? t("motionPage.reduceStatusOn") : t("motionPage.reduceStatusOff")}
                    type={isReduceMotionEnabled ? "info" : "neutral"}
                    spreadBehaviour="free"
                />
            </div>
            <div
                style={styles.stage}
            >
                <Animated.View
                    style={getSlideStyle({
                        offset: spaces.spacingXl,
                        isReduceMotionEnabled,
                        axis: "x",
                        progress
                    })}
                >
                    <Badge
                        title={t("motionPage.reduceDemoLabel")}
                        spreadBehaviour="free"
                        type="info"
                    />
                </Animated.View>
            </div>
            <Button
                title={isShown ? t("motionPage.reduceDemoHide") : t("motionPage.reduceDemoShow")}
                onPress={() => setIsShown(!isShown)}
                spreadBehaviour="free"
                variant="outline"
            />
        </div>
    </Preview>;
};

export const getMotionToc = (t: DocsTranslate): Array<DocsTocItem> => [
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("motionPage.intentsTitle"),
        id: "intents"
    },
    {
        title: t("motionPage.durationsTitle"),
        id: "durations"
    },
    {
        title: t("motionPage.curvesTitle"),
        id: "easings"
    },
    {
        title: t("motionPage.springTitle"),
        id: "spring"
    },
    {
        title: t("motionPage.rulesTitle"),
        id: "rules"
    },
    {
        title: t("motionPage.componentsTitle"),
        id: "components"
    },
    {
        title: t("motionPage.pressTitle"),
        id: "press"
    },
    {
        title: t("motionPage.toggleTitle"),
        id: "toggle"
    },
    {
        title: t("motionPage.appearTitle"),
        id: "appear"
    },
    {
        title: t("motionPage.listsTitle"),
        id: "lists"
    },
    {
        title: t("motionPage.swapTitle"),
        id: "swap"
    },
    {
        title: t("motionPage.surfacesTitle"),
        id: "surfaces"
    },
    {
        title: t("motionPage.iconsTitle"),
        id: "icons"
    },
    {
        title: t("motionPage.reduceTitle"),
        id: "reduce-motion"
    },
    {
        title: t("motionPage.themeTitle"),
        id: "themes"
    },
    {
        title: t("motionPage.helpersTitle"),
        id: "helpers"
    }
];

const MotionPage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const locale = getKitLocale(language);

    const styles = useMotionStyles();

    const {
        durations
    } = NCoreUIKitTheme.useContext();

    const getIntentTime = (intent: MotionIntentType | ExtraIntentType, row: IntentRowType) => {
        if(isMotionIntent(intent)) {
            const {
                duration,
                easing
            } = MOTION_INTENTS[intent];

            return [
                `${duration} · ${durations[duration]}\u00A0ms`,
                easing
            ];
        }

        return [
            row.timeKey ? t(row.timeKey) : row.time ?? "—"
        ];
    };

    const intentTableRows = INTENT_PURPOSES.map(({
        intent,
        key
    }) => {
        const {
            duration,
            easing,
            motion
        } = MOTION_INTENTS[intent];

        const reducedDuration = getMotionDuration({
            isReduceMotionEnabled: true,
            durations,
            duration,
            motion
        });

        return [
            <InlineCode
                key="intent"
            >
                {intent}
            </InlineCode>,
            t(key),
            <div
                style={styles.intentItem}
                key="timing"
            >
                <Text
                    variant="bodySmallSize"
                    color="mid"
                >
                    {`${duration} · ${durations[duration]}\u00A0ms`}
                </Text>
                <Text
                    variant="bodySmallSize"
                    color="low"
                >
                    {easing}
                </Text>
            </div>,
            `${motion} · ${reducedDuration}\u00A0ms`
        ];
    });

    const componentTableRows = INTENT_ROWS.map((row) => [
        <div
            style={styles.intent}
            key="intent"
        >
            {
                row.intents.map((intent) => <div
                    style={styles.intentItem}
                    key={intent}
                >
                    <InlineCode>
                        {intent}
                    </InlineCode>
                    {
                        getIntentTime(intent, row).map((line, index) => <Text
                            color={index === 0 ? "mid" : "low"}
                            variant="bodySmallSize"
                            key={line}
                        >
                            {line}
                        </Text>)
                    }
                </div>)
            }
        </div>,
        row.whereKey ? t(row.whereKey) : row.where,
        t(row.moveKey, row.moveDurations ? row.moveDurations.map((name) => durations[name]) : row.moveArgs?.map((value) => formatDecimal(value, locale))),
        t(row.reduceKey, [
            durations.short
        ])
    ]);

    const staggerSpread = CHART_STAGGER_STEPS * durations.stagger;

    const ruleArgs = [
        durations.short,
        durations.medium,
        formatDecimal(MOTION_PRESS_SCALE, locale),
        durations.autoplay / MS_PER_SECOND,
        durations.autoplay * NOTICE_MAXIMUM_RATIO / MS_PER_SECOND
    ];

    const rules = Array.from({
        length: RULE_COUNT
    }, (_item, index) => t(`motionPage.rule${index + 1}`, ruleArgs));

    return <Article>
        <PageHeader
            breadcrumbs={[
                {
                    label: t("breadcrumb.docs"),
                    href: "/"
                },
                {
                    label: t("nav.sectionFoundations")
                }
            ]}
            lead={t("foundation.motionLead")}
            title={t("page.motion")}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("motionPage.overviewText")}
            </Paragraph>
            <BulletList
                items={[
                    t("motionPage.overviewIntent", [
                        INTENT_PURPOSES.length
                    ]),
                    t("motionPage.overviewToken", [
                        durations.short,
                        durations.medium,
                        durations.long
                    ]),
                    t("motionPage.overviewReduce", [
                        durations.short
                    ]),
                    t("motionPage.overviewPlain")
                ]}
            />
        </Section>
        <Section
            title={t("motionPage.intentsTitle")}
            id="intents"
        >
            <Paragraph>
                {t("motionPage.intentsText")}
            </Paragraph>
            <MotionTable
                headers={[
                    t("motionPage.colIntentName"),
                    t("motionPage.colPurpose"),
                    t("motionPage.colTiming"),
                    t("motionPage.colReduced")
                ]}
                rows={intentTableRows}
            />
            <Paragraph>
                {t("motionPage.intentsReduceText", [
                    durations.short
                ])}
            </Paragraph>
        </Section>
        <Section
            title={t("motionPage.durationsTitle")}
            id="durations"
        >
            <Paragraph>
                {t("motionPage.durationsText")}
            </Paragraph>
            <MotionTable
                rows={DURATION_TOKENS.map((token) => [
                    <InlineCode
                        key={token.name}
                    >
                        {token.name}
                    </InlineCode>,
                    <InlineCode
                        key={`${token.name}-value`}
                    >
                        {`${durations[token.name]}\u00A0ms`}
                    </InlineCode>,
                    t(token.key)
                ])}
                headers={[
                    t("motionPage.colToken"),
                    t("motionPage.colValue"),
                    t("motionPage.colUsedFor")
                ]}
            />
            <Paragraph>
                {t("motionPage.durationsWhyText")}
            </Paragraph>
            <Paragraph>
                {t("motionPage.durationsStaggerText", [
                    durations.stagger,
                    staggerSpread,
                    durations.long + staggerSpread
                ])}
            </Paragraph>
        </Section>
        <Section
            title={t("motionPage.curvesTitle")}
            id="easings"
        >
            <Paragraph>
                {t("motionPage.curvesText")}
            </Paragraph>
            <EasingsPreview/>
        </Section>
        <Section
            title={t("motionPage.springTitle")}
            id="spring"
        >
            <Paragraph>
                {t("motionPage.springText")}
            </Paragraph>
            <Stats
                items={[
                    {
                        value: String(MOTION_SPRINGS.standard.stiffness),
                        label: t("motionPage.springStiffness")
                    },
                    {
                        value: String(MOTION_SPRINGS.standard.damping),
                        label: t("motionPage.springDamping")
                    },
                    {
                        value: String(MOTION_SPRINGS.standard.mass),
                        label: t("motionPage.springMass")
                    },
                    {
                        value: `≈ ${formatDecimal(SPRING_RATIO, locale)}`,
                        label: t("motionPage.springRatio")
                    }
                ]}
            />
            <Paragraph>
                {t("motionPage.springRatioText", [
                    formatDecimal(SPRING_RATIO, locale),
                    SPRING_SETTLE_MS
                ])}
            </Paragraph>
            <SpringPreview/>
            <CodeBlock
                code={SPRING_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("motionPage.rulesTitle")}
            id="rules"
        >
            <Paragraph>
                {t("motionPage.rulesText")}
            </Paragraph>
            <RuleList
                items={rules}
            />
        </Section>
        <Section
            title={t("motionPage.componentsTitle")}
            id="components"
        >
            <Paragraph>
                {t("motionPage.componentsText")}
            </Paragraph>
            <MotionTable
                headers={[
                    t("motionPage.colIntent"),
                    t("motionPage.colWhere"),
                    t("motionPage.colMoves"),
                    t("motionPage.colReduce")
                ]}
                rows={componentTableRows}
            />
        </Section>
        <Section
            title={t("motionPage.pressTitle")}
            id="press"
        >
            <Paragraph>
                {t("motionPage.pressText", [
                    formatDecimal(MOTION_PRESS_SCALE, locale),
                    durations.short
                ])}
            </Paragraph>
            <PressDemo/>
            <CodeBlock
                code={PRESS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("motionPage.toggleTitle")}
            id="toggle"
        >
            <Paragraph>
                {t("motionPage.toggleText", [
                    durations.short
                ])}
            </Paragraph>
            <ToggleDemo/>
            <CodeBlock
                code={TOGGLE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("motionPage.appearTitle")}
            id="appear"
        >
            <Paragraph>
                {t("motionPage.appearText", [
                    formatDecimal(MOTION_ICON_APPEAR_SCALE, locale),
                    MOTION_ICON_APPEAR_BLUR,
                    durations.medium
                ])}
            </Paragraph>
            <AppearDemo/>
            <CodeBlock
                code={APPEAR_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("motionPage.listsTitle")}
            id="lists"
        >
            <Paragraph>
                {t("motionPage.listsText", [
                    durations.medium,
                    durations.short
                ])}
            </Paragraph>
            <ListDemo/>
            <CodeBlock
                code={LIST_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("motionPage.swapTitle")}
            id="swap"
        >
            <Paragraph>
                {t("motionPage.swapText", [
                    durations.medium
                ])}
            </Paragraph>
            <SwapDemo/>
            <CodeBlock
                code={SWAP_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("motionPage.surfacesTitle")}
            id="surfaces"
        >
            <Paragraph>
                {t("motionPage.surfacesText", [
                    formatDecimal(MOTION_SURFACE_SCALE, locale),
                    durations.short
                ])}
            </Paragraph>
            <SurfaceDemo/>
        </Section>
        <Section
            title={t("motionPage.iconsTitle")}
            id="icons"
        >
            <Paragraph>
                {t("motionPage.iconsText")}
            </Paragraph>
            <BulletList
                items={[
                    t("motionPage.iconRuleMorph"),
                    t("motionPage.iconRuleAppear"),
                    t("motionPage.iconRuleSignature"),
                    t("motionPage.iconRuleStill")
                ]}
            />
            <SubTitle>
                {t("motionPage.iconsMorphTitle")}
            </SubTitle>
            <Paragraph>
                {t("motionPage.iconsMorphText", [
                    MORPH_PAIRS.length
                ])}
            </Paragraph>
            <MorphGallery/>
            <CodeBlock
                code={MORPH_CODE}
                language="tsx"
            />
            <SubTitle>
                {t("motionPage.iconsSignatureTitle")}
            </SubTitle>
            <Paragraph>
                {t("motionPage.iconsSignatureText")}
            </Paragraph>
            <SignatureDemo/>
            <CodeBlock
                code={SIGNATURE_CODE}
                language="tsx"
            />
            <Cards
                items={[
                    {
                        text: t("motionPage.iconsLinkText"),
                        href: "/components/animated-icon",
                        title: "AnimatedIcon"
                    }
                ]}
            />
        </Section>
        <Section
            title={t("motionPage.reduceTitle")}
            id="reduce-motion"
        >
            <Paragraph>
                {t("motionPage.reduceText")}
            </Paragraph>
            <MotionTable
                rows={[
                    [
                        t("motionPage.reduceMovement"),
                        t("motionPage.reduceMovementResult", [
                            durations.short
                        ])
                    ],
                    [
                        t("motionPage.reduceFadeKind"),
                        t("motionPage.reduceFadeResult", [
                            durations.short
                        ])
                    ],
                    [
                        t("motionPage.reduceLoading"),
                        t("motionPage.reduceLoadingResult")
                    ],
                    [
                        t("motionPage.reduceAttention"),
                        t("motionPage.reduceAttentionResult")
                    ],
                    [
                        t("motionPage.reduceAutoplay"),
                        t("motionPage.reduceAutoplayResult")
                    ],
                    [
                        t("motionPage.reduceSpring"),
                        t("motionPage.reduceSpringResult")
                    ]
                ]}
                headers={[
                    t("motionPage.reduceColType"),
                    t("motionPage.reduceColResult")
                ]}
            />
            <ReduceMotionDemo/>
        </Section>
        <Section
            title={t("motionPage.themeTitle")}
            id="themes"
        >
            <Paragraph>
                {t("motionPage.themeText")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("motionPage.themeCodeText")}
            </Paragraph>
            <CodeBlock
                code={THEME_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("motionPage.helpersTitle")}
            id="helpers"
        >
            <Paragraph>
                {t("motionPage.helpersText")}
            </Paragraph>
            <MotionTable
                rows={HELPER_ROWS.map((row) => [
                    <div
                        key={row.names.join()}
                        style={styles.stack}
                    >
                        {
                            row.names.map((name) => <InlineCode
                                key={name}
                            >
                                {name}
                            </InlineCode>)
                        }
                    </div>,
                    t(row.kindKey),
                    t(row.textKey)
                ])}
                headers={[
                    t("motionPage.helpersColName"),
                    t("motionPage.helpersColKind"),
                    t("motionPage.helpersColWhat")
                ]}
            />
            <SubTitle>
                useMotionTiming
            </SubTitle>
            <Paragraph>
                {t("motionPage.helpersTimingText", [
                    durations.short
                ])}
            </Paragraph>
            <CodeBlock
                code={TIMING_CODE}
                language="tsx"
            />
            <SubTitle>
                useAnimatedState
            </SubTitle>
            <Paragraph>
                {t("motionPage.helpersAnimatedStateText")}
            </Paragraph>
            <CodeBlock
                code={ANIMATED_STATE_CODE}
                language="tsx"
            />
            <SubTitle>
                usePresence
            </SubTitle>
            <Paragraph>
                {t("motionPage.helpersPresenceText", [
                    durations.short
                ])}
            </Paragraph>
            <CodeBlock
                code={PRESENCE_CODE}
                language="tsx"
            />
            <SubTitle>
                getCrossfadeStyle
            </SubTitle>
            <Paragraph>
                {t("motionPage.helpersCrossfadeText")}
            </Paragraph>
            <CodeBlock
                code={CROSSFADE_CODE}
                language="tsx"
            />
            <SubTitle>
                getMotionTransition
            </SubTitle>
            <Paragraph>
                {t("motionPage.helpersTransitionText")}
            </Paragraph>
            <CodeBlock
                code={TRANSITION_CODE}
                language="tsx"
            />
        </Section>
    </Article>;
};
export default MotionPage;
